import { queryOptions, useQuery } from '@tanstack/react-query';
import { ItemsApi } from '@/lib/api/common/items';
import type { BulkFetchV2Response } from '@/lib/api/types/items';
import { commonQueryKeys } from '@/lib/query-keys/common';

// Default to all fields for backwards compatibility, we should remove fields that are not needed by consumers of this query in the future to reduce payload size and improve performance.
export const DEFAULT_FIELD_IDS = [
  'name',
  'description',
  'itemTypeId',
  'itemStatusId',
  'parentId',
  'teamId',
  'earliestStartDateTime',
  'deadlineDateTime',
  'plannedStartDateTime',
  'plannedFinishDateTime',
  'doneDateTime',
  'externalKey',
  'externalLink',
  'estimationId',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'dependencies',
  'customFields',
];

export const itemsBulkFetchV2QueryOptions = (
  {
    itemIds,
    customFieldIds,
    fieldIds,
  }: {
    itemIds: number[];
    customFieldIds?: number[];
    fieldIds?: string[];
  },
  staleTime?: number,
  enabled = true,
) =>
  queryOptions<BulkFetchV2Response>({
    // The key omits undefined values (React Query hashes via JSON.stringify), so
    // default-field callers keep their legacy key while an explicit narrower
    // field set caches separately.
    queryKey: commonQueryKeys.itemsBulkFetchV2.byParams({ ids: itemIds, customFieldIds, fieldIds }),
    queryFn: () =>
      ItemsApi.getItemsBulkFetchV2Chunked({
        ids: itemIds,
        fieldIds: fieldIds ?? DEFAULT_FIELD_IDS,
        customFieldIds,
      }),
    enabled,
    ...(staleTime !== undefined && { staleTime }),
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
  });

export const useItemsBulkFetchV2Query = (
  {
    itemIds,
    customFieldIds,
    fieldIds = DEFAULT_FIELD_IDS,
  }: {
    itemIds: number[];
    customFieldIds?: number[];
    fieldIds?: string[];
  },
  staleTime?: number,
  enabled = true,
) =>
  useQuery<BulkFetchV2Response>({
    queryKey: commonQueryKeys.itemsBulkFetchV2.byParams({ ids: itemIds, customFieldIds, fieldIds }),
    queryFn: () =>
      ItemsApi.getItemsBulkFetchV2Chunked({
        ids: itemIds,
        fieldIds,
        customFieldIds,
      }),
    enabled,
    ...(staleTime !== undefined && { staleTime }),
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
  });
